import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import DocumentControlPanel from './DocumentControlPanel/DocumentControlPanel';
import PDFDocument from './PDFDocument';
import PreviewBanner from './PreviewBanner';

// Most of react-virtualized's styles are functional (eg position, size).
// Functional styles are applied directly to DOM elements.
// The Table component ships with a few presentational styles as well.
// They are optional, but if you want them you will need to also import the CSS file.
// This only needs to be done once; probably during your application's bootstrapping process.
import 'react-virtualized/styles.css';
import { DocumentCallback, PageCallback } from 'react-pdf/dist/cjs/shared/types';

// File
export const SMALL_PDF_SIZE_THRESHOLD = 5000; //kb

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url,
  ).toString();
type Props  ={
    source: string;
    previewSource?: string;
    page?: number;
    loadingText?: string;
    highlightContent?: string;
}

const PDFViewer: FunctionComponent<Props> = ({
  source ,
  previewSource: previewSourceProps = '',
  highlightContent = '',
  loadingText,
  page: initialPage = 1

}) => {
    const [totalPages, setTotalPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(initialPage);
    const [pagesInViewport, setPagesInViewport] = useState<number[]>([]);
    const [pagesInViewportSet, setPagesInViewportSet] = useState<Set<number>>( new Set());
    // const [goToPage, setGoToPage] = useState<number>();
    const [scale, setScale] = useState<number>(1.0);
    const [isFullDocRendered, setIsFullDocRendered] = useState(true);
    const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
    const [fullDocSource, setFullDocSource] = useState<string>('');
    const [previewSource, setPreviewSource] = useState<string>('');
    const [showFullDoc, setShowFullDoc] = useState<boolean>(true);
    const [percentLoaded, setPercentLoaded] = useState<number>(0);
    const [pageRotateStepsMap, setPageRotateStepsMap] = useState<object>({});
    const isPreviewError = useRef<boolean>(false);
    const ref = useRef();

    const handleFullDocumentLoadSuccess = (doc:DocumentCallback) => {
        setTotalPages(doc.numPages);
    };

    const handlePagesRenderSuccess = (page: PageCallback) => {
        if(page.pageNumber === initialPage && !isFullDocRendered){
            setShowFullDoc(true);
        }
    };

    const onPreviewLoadSuccess = () => {
        setIsPreviewLoaded(true);
    };

    const onLoadProgress = ({ loaded, total }) => {
        if (
            previewSource &&
            !isPreviewError.current &&
            total > SMALL_PDF_SIZE_THRESHOLD * 1000
        ) {
            setShowFullDoc(false);
        }
        setPercentLoaded(Math.trunc((loaded / total) * 100));
    };

    const handlePreviewError = (error: Error, errorType: string) => {
        isPreviewError.current = true;
        setShowFullDoc(true);
        console.error('Preview error: ', errorType, error);
    };

    const handleFullDocError = (error: Error, errorType) => {
        console.error('Full document error: ',errorType, error);
    };

    useEffect(() => {
        if (source === fullDocSource) {
            // if (isFullDocRendered) {
            setCurrentPage(initialPage);
            // } else {
            //     setShowFullDoc(false);
            //     setPreviewSource(previewSourceProps);
            // }
        } else {
            setFullDocSource(source);
            setPreviewSource(previewSourceProps);
            setIsFullDocRendered(false);
            setIsPreviewLoaded(false);
            setPagesInViewport([]);
            setTotalPages(0);
            setCurrentPage(initialPage);
            // setGoToPage(null);
            setShowFullDoc(true);
            setPercentLoaded(0);
            setPagesInViewportSet(new Set());
            setPageRotateStepsMap({});
            isPreviewError.current = false;
        }
        setScale(1.0);
    }, [source, initialPage]);

    // TODO : Implement the with for web also
    // * This feature is use to get the current page on Scroll
    // const onPageInViewPort = (inViewPort: boolean, pageNum: number) => {
    //     if (
    //         !isFullDocRendered ||
    //         (!inViewPort && !pagesInViewportSet.has(pageNum))
    //     ) {
    //         return;
    //     }

    //     let newPagesInViewport = [];
    //     if (inViewPort) {
    //         if (pageNum < currentPage) {
    //             if (currentPage - pageNum > 1) {
    //                 // Fixes wrong prev page num
    //                 newPagesInViewport = [pageNum, ...pagesInViewport.slice(1)];
    //             } else {
    //                 newPagesInViewport = [pageNum, ...pagesInViewport];
    //             }
    //         } else {
    //             if (pageNum - currentPage > 3) {
    //                 // Fixes wrong prev page num
    //                 newPagesInViewport = [pageNum, ...pagesInViewport.slice(1)];
    //             } else {
    //                 newPagesInViewport = [...pagesInViewport, pageNum];
    //             }
    //         }
    //     } else {
    //         newPagesInViewport = pagesInViewport.filter(
    //             (page) => page !== pageNum
    //         );
    //     }
    //     if (newPagesInViewport[0]) {
    //         setCurrentPage(newPagesInViewport[0]);
    //     }
    //     setPagesInViewportSet(new Set(newPagesInViewport));
    //     setPagesInViewport(newPagesInViewport);
    // };

    const handleOnRotateChange = (value: number) => {
        setPageRotateStepsMap({
            ...pageRotateStepsMap,
            [currentPage]:(pageRotateStepsMap[currentPage] || 0) + value
        });
    };

    return (
        <div className="pdf-viewer">
            <PreviewBanner
                show={isPreviewLoaded && !showFullDoc}
                percentLoaded={percentLoaded}
                text={loadingText}
            />
            <div className="pdf-viewer__document-container" ref={ref}>
                {!showFullDoc && (
                    <PDFDocument
                        path={previewSource}
                        scale={scale}
                        onActionError={handlePreviewError}
                        contentToHighlight={highlightContent}
                        onLoadSuccess={onPreviewLoadSuccess}
                    />
                )}
                <PDFDocument
                    path={fullDocSource}
                    visible={showFullDoc}
                    onActionError={handleFullDocError}
                    currentPage={currentPage}
                    scale={scale}
                    // onPageInViewPort={onPageInViewPort}
                    onPageRenderSuccess={handlePagesRenderSuccess}
                    onLoadSuccess={handleFullDocumentLoadSuccess}
                    onLoadProgress={onLoadProgress}
                    parentElement={ref.current}
                    contentToHighlight={highlightContent}
                    pageToHighlight={initialPage}
                    pageRotateStepsMap={pageRotateStepsMap}
                />
            </div>
            {isFullDocRendered && false  && (
                <DocumentControlPanel
                    page={currentPage}
                    scale={scale}
                    onScaleChange={setScale}
                    totalPage={totalPages}
                    onPageChange={setCurrentPage}
                    onRotateChange={handleOnRotateChange}
                />
            )}
        </div>
    );
};

export default PDFViewer;
